import React, { useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";

import MyBooking from "./MyBooking";
import MyProfile from "./MyProfile";
import WishList from "./WishList";
import { useTranslation } from "react-i18next";
import DeleteAccount from "./DeleteAccount";
import EmptyCart from "./EmptyCart";
import { useTranslationEx } from "../../hooks/useTranslationEx";
import { useMounted } from "../../hooks/useMounted";

// Define the type for the available tabs
type Tab =
  | "profile"
  | "booking"
  | "wishlist"
  | "inquiry"
  | "cart"
  | "deleteAccount";

// Define the content for each tab
const tabContent: Record<Tab, JSX.Element> = {
  profile: <MyProfile />,
  booking: <MyBooking />,
  wishlist: <WishList />,
  inquiry: <p>Submit your inquiries here.</p>,
  cart: <EmptyCart />,
  deleteAccount: <DeleteAccount />,
};

const MyPage: React.FC = () => {
  const { t } = useTranslation();
  const { tab } = useParams<{ tab?: Tab }>(); // Extract the tab from the URL
  const navigate = useNavigate();
  const { mounted } = useMounted();
  // Validate and set the active tab, defaulting to "profile"
  const activeTab: Tab =
    tab && Object.keys(tabContent).includes(tab) ? (tab as Tab) : "profile";

  // Redirect to the default tab if the `tab` parameter is invalid
  if (tab && !Object.keys(tabContent).includes(tab)) {
    return <Navigate to="/mypage/profile" replace />;
  }

  // Handle navigation when a tab is clicked
  const handleTabChange = (newTab: Tab) => {
    if (newTab === "cart") {
      navigate(`/shopping-cart`);
      return;
    }
    if (newTab === "inquiry") {
      navigate(`/my-inquiry`);
      return;
    }
    navigate(`/mypage/${newTab}`);
  };

  return (
    <div className="ui-container med-one mt-6 flex">
      {/* Left Navigation */}
      <nav className="wei-1 hidden max-w-40 bg-white p-6 sm:block">
        <p className="text-[1.5rem] font-semibold">{t("My Page")}</p>
        <div className="my-6 h-[0.0625rem] w-full bg-[#E0E0E0]" />
        {mounted && (
          <ul className="space-y-6">
            {(
              [
                "profile",
                "booking",
                "wishlist",
                "inquiry",
                "cart",
                "deleteAccount",
              ] as Tab[]
            ).map((item) => (
              <li
                key={item}
                className={`cursor-pointer text-base font-semibold ${
                  activeTab === item ? "text-[#007AFF]" : "text-[#232323]"
                }`}
                onClick={() => handleTabChange(item)}
              >
                {item === "profile"
                  ? t("My Profile")
                  : item === "booking"
                    ? t("My Booking")
                    : item === "wishlist"
                      ? t("Wishlist")
                      : item === "inquiry"
                        ? t("My Inquiry")
                        : item === "deleteAccount"
                          ? t("Delete account")
                          : t("My Cart")}
              </li>
            ))}
          </ul>
        )}
      </nav>
      {/* Right Content */}
      <main className="sm:ml-10 sm:flex-1">
        <div className="bg-white sm:p-6">{tabContent[activeTab]}</div>
      </main>
    </div>
  );
};

export default MyPage;
